<template>
  <div>
    <page-header
      left-side-class="col-xl-4"
      :page="page"
      right-side-class="col-xl-8 align-items-end d-flex pb-2 justify-content-end"
    >
      <template #customActions>
        <action-button
          v-if="canUploadDocuments"
          i="fas fa-file-plus"
          text="Cargar Documentos"
          variant="success"
          @click="onShowUploadFiles"
        />

        <input
          ref="uploadFiles"
          accept=".pdf"
          multiple
          style="display:none"
          type="file"
          @change="onUploadFiles"
        />
      </template>
    </page-header>
    <filter-manager
      ref="certificationLoadFilterRef"
      v-model="filter"
      :filters="filterManagerConfig.basic"
      :search-text="null"
      @academicYear="onChangeAcademicYear"
      @center="onChangeCenter"
      @month="onChangeMonth"
      @service="onChangeService"
      @specialist="onChangeSpecialist"
      @specialistsOrCompanies="onChangeSpecialistsOrCompanies"
      @status="onChangeStatus"
      @therapyDocumentType="onChangeTherapyDocumentType"
    >
      <div class="main-box col-md-10">
        <i class="fas fa-bell-exclamation" />
        <span>
          Recordatorio: Es importante actualizar la factura antes de comenzar el
          proceso de carga de certificaciones.
        </span>
      </div>
      <div class="col-md mt-3 text-right">
        <action-button
          text="Buscar"
          variant="success px-4"
          @click="onSearchHandler"
        />
      </div>
    </filter-manager>
    <custom-separator class="pt-3 mb-3" text="Resultado de Certificaciones" />
    <div v-if="results.length > 0">
      <div class="row">
        <div class="col">
          <div class="info-container mt-3">
            <div class="d-flex align-items-center col">
              <div class="dot color-orange">
                <em class="title-icon fa fa-user-circle" />
              </div>
              <div class="d-flex flex-column">
                <div class="color-gray-6 font-14">Especialista</div>
                <div class="color-black font-16 font-weight-bold">
                  {{ specialistInfo.name }}
                </div>
              </div>
            </div>
            <div class="col">
              <div class="d-flex align-items-center">
                <em class="fa fa-user-friends color-gray-6 mr-2" />
                <div class="color-gray-6 font-14">Total Estudiantes:</div>
              </div>
              <div class="color-black font-25 ml-4">
                {{ specialistInfo.totalStudent }}
              </div>
            </div>
            <div class="col">
              <div class="d-flex align-items-center">
                <em class="fa fa-check-circle color-primary-1 mr-2" />
                <div class="color-gray-6 font-14">Documentos Cargados:</div>
              </div>
              <div class="color-black font-25 ml-4">
                {{ specialistInfo.succeedTotal }}
              </div>
            </div>
            <div class="col">
              <div class="d-flex align-items-center">
                <em class="fa fa-clock color-yellow-star mr-2" />
                <div class="color-gray-6 font-14">Documentos Pendientes:</div>
              </div>
              <div class="color-black font-25 ml-4">
                {{ specialistInfo.pendingTotal }}
              </div>
            </div>
            <div v-if="specialistInfo.errorDocumentCount > 0" class="col">
              <div class="d-flex align-items-center">
                <em class="fa fa-times-circle color-primary-4 mr-2" />
                <div class="color-gray-6 font-14">Documentos Con Error:</div>
              </div>
              <div class="d-flex align-items-center">
                <div class="color-black font-25 ml-4 mr-4">
                  {{ specialistInfo.errorDocumentCount }}
                </div>
                <u
                  v-if="specialistInfo.errorDocumentCount > 0"
                  class="color-green cursor-pointer"
                  @click="showError = true"
                >
                  Ver Detalles
                </u>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="border-bottom my-3" />
      <div v-if="showError && specialistInfo.errorDocumentCount > 0">
        <file-error
          :errors="specialistInfo.errorMessages"
          @close="showError = false"
        />
      </div>
      <div class="mt-3">
        <validation-observer ref="editDocumentForm" novalidate tag="form">
          <div
            v-for="(item, index) in resultsData"
            :key="'massive-certification' + index"
          >
            <custom-callout
              :border-color="getColor(documentStatusId(item))"
              class="mb-3 custom-callout"
              :show-edition="item.isOpen"
            >
              <div class="row">
                <div class="col-md-2 col-sm-12 icon-container border-right">
                  <custom-balloon
                    :background-color="getColor(documentStatusId(item))"
                    class="mt-2 p-2 font-weight-normal font-16"
                    :text="getStatusName(documentStatusId(item))"
                  />
                </div>
                <div class="col-md-3 col-sm-12 col-spacing student-spacing">
                  <div class="legend">SIE:</div>
                  <div class="font-weight-normal text-black">
                    {{ item.studentSieId }}
                  </div>
                </div>
                <div
                  class="col-md-3 col-sm-12 col-spacing service-place-spacing"
                >
                  <div class="legend">Estudiante:</div>
                  <div class="font-weight-normal text-black">
                    {{ item.studentName }}
                  </div>
                </div>
                <div class="col-md-2 col-sm-12 col-spacing">
                  <div v-if="item.documentName" class="legend">Documento:</div>
                  <div class="font-weight-normal text-black">
                    {{ item.documentName }}
                  </div>
                </div>
                <div
                  class="col-md-2 col-sm-12 col-spacing d-flex justify-content-end"
                >
                  <action-button
                    class="ml-0"
                    :disabled="disabledActionButton(documentStatusId(item))"
                    i="fas fa-file-download"
                    :variant="getDownloadActionColor(item)"
                    @click="onDownloadDocument(item)"
                  />
                  <action-button
                    v-if="hideActionButton(item.billingStatusId)"
                    class="ml-2"
                    :disabled="disabledActionButton(documentStatusId(item))"
                    i="fas fa-file-edit"
                    :variant="getEditActionColor(item)"
                    @click="onToggleEdit(item, index)"
                  />
                </div>
              </div>
              <template
                v-if="item.isOpen"
                #edition
                class="p-3 pb-2 custom-callout-edition"
              >
                <div class="info-panel">
                  <div class="row">
                    <div class="col">
                      <div class="info-panel-main-title">
                        <div
                          class="dot color-orange"
                          style="height:25px; height:25px;"
                        >
                          <em class="title-icon fa fa-file-edit font-14" />
                        </div>
                        <span class="ml-n1">Editar Documento</span>
                      </div>
                    </div>
                  </div>
                  <div class="info-panel-container">
                    <div class="row px-3">
                      <div class="col-md-4">
                        <div class="color-secondary-6">
                          Tipo de Documento:
                        </div>
                        <validation-provider
                          v-slot="{ errors }"
                          rules="required"
                        >
                          <custom-drop-down
                            v-model="item.documentType"
                            :clearable="false"
                            custom-error-msg="Favor de seleccionar el tipo el documento."
                            :error="errors[0]"
                            fieldkey="id"
                            fieldname="name"
                            :options="therapyDocumentTypes"
                            placeholder="Seleccione"
                          />
                        </validation-provider>
                      </div>
                      <div class="col-md-4">
                        <div class="color-secondary-6">Archivo</div>
                        <validation-provider
                          v-slot="{ errors }"
                          rules="required"
                        >
                          <custom-file-input
                            ref="attachedDocument"
                            v-model="item.selectedDocument"
                            :accept="'.pdf'"
                            custom-error-msg="Favor de anejar un documento."
                            display-style="normal"
                            :error="errors[0]"
                            label-position="left"
                            required
                          />
                        </validation-provider>
                      </div>
                      <div class="col-md-4 text-right mt-3">
                        <action-button
                          i="fas fa-times-circle"
                          variant="outline-danger btn-cancel danger m-2"
                          @click="onCancelEdit(item, index)"
                        >
                          Cancelar
                        </action-button>
                        <action-button
                          variant="success m-2 pl-4 pr-4"
                          @click="handleSave(item, index)"
                        >
                          Guardar
                        </action-button>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </custom-callout>
          </div>
        </validation-observer>
      </div>
    </div>
    <div v-else class="col-12 p-4 text-center align-items-center no-info mt-4">
      <div class="row justify-content-center">
        <div class="col-12 col-lg-1 align-self-center">
          <i class="far fa-folder-open icon no-info-icon pl-2" />
        </div>
        <div class="col-12 col-lg-3 ml-lg-n4 align-self-center">
          <span class="no-info-text">No hay información disponible.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { serialize as objectToFormData } from 'object-to-formdata';
import PageHeader from 'PageHeader';
import CustomSeparator from '@/components/common/CustomSeparator';
import CustomCallout from '@/components/common/CustomCallout';
import { ActionButton, CustomDropDown } from '@/components/common';
import CustomFileInput from '@/components/common/CustomFileInput';
import CustomBalloon from '@/components/common/CustomBalloon';

import { FileError } from './components';
import { Colors } from '@/components/common';
import alertMixin from '@/mixins/alert.mixin.js';
import statusTypes from '@/utils/constants/statusTypes.js';
import _generalApi from '@/modules/app/api.js';

import { name } from '@/modules/certificationLoad/store';
import {
  ACTION_TYPES,
  MUTATION_TYPES,
} from '@/modules/certificationLoad/store/types';
import { createNamespacedHelpers } from 'vuex';
const {
  mapActions,
  mapState,
  mapGetters,
  mapMutations,
} = createNamespacedHelpers(name);
import billingStatus from '@/utils/constants/billingStatus.js';
import roles from '@/utils/constants/roles.js';
import { getList as _getServiceLocations } from '@/modules/shared/servicelocation.api';

export default {
  name: 'ProviderMassiveCertificationLoad',
  components: {
    PageHeader,
    CustomSeparator,
    CustomCallout,
    ActionButton,
    CustomBalloon,
    FileError,
    CustomDropDown,
    CustomFileInput,
  },
  mixins: [alertMixin],
  data: () => ({
    showError: false,
    info: {},
    page: {
      title: 'Carga de Certificaciones en Masa',
      icon: 'fas fa-user-tie',
      routes: ['Proveedor', 'Carga de Certificaciones en Masa'],
    },
    statusList: [statusTypes.Pendiente, statusTypes.Cargado],
    filter: {
      academicYear: null,
      month: null,
      service: null,
      center: null,
      specialist: null,
      specialistInfo: {
        name: '',
      },
      results: [],
      resultsData: [],
      resultsChunked: [],
      therapyDocumentType: null,
      specialistsOrCompanies: null,
      isAdminOrSpecialist: false,
      status: null,
      locationService: null,
    },
    catalog: {
      academicYears: [],
      months: [],
      centers: [],
      services: [],
      specialists: [],
      documentTypes: [],
      therapyDocumentTypes: [],
      specialistsOrCompanies: [],
      statusList: [],
      locationServices: [],
    },
    filterSnapshot: {
      academicYear: null,
      month: null,
      service: null,
      center: null,
      specialist: null,
      specialistInfo: {
        name: '',
      },
      results: [],
      resultsData: [],
      resultsChunked: [],
      therapyDocumentType: null,
      specialistsOrCompanies: null,
      isAdminOrSpecialist: false,
      status: null,
    },
  }),
  computed: {
    ...mapGetters({
      pagination: 'getPagination',
    }),
    ...mapState({
      academicYears: (state) => state.catalog.academicYears,
      months: (state) => state.catalog.months,
      centers: (state) => state.catalog.centers,
      specialists: (state) => state.catalog.specialists,
      services: (state) => state.catalog.services,
      documentTypes: (state) => state.catalog.documentTypes,
      resultsModel: (state) => state.filter.results,
      resultsData: (state) => state.filter.resultsData,
      specialistInfo: (state) => state.filter.specialistInfo,
      currentFilter: (state) => state.currentFilter,
      specialistsOrCompanies: (state) =>
        state.catalog.specialistsOrCompanies.data,
      therapyDocumentTypes: (state) => state.catalog.therapyDocumentTypes.data,
    }),
    filterManagerConfig() {
      return {
        basic: [
          {
            name: 'academicYear',
            component: 'CustomDropDown',
            label: 'Año Fiscal:',
            options: this.academicYears,
            initialValue: this.filter.academicYear,
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: false,
            validations: 'required',
            error: 'El campo Proveedor es obligatorio',
            class: 'col-md-3',
          },
          {
            name: 'month',
            component: 'CustomDropDown',
            label: 'Mes:',
            options: this.months,
            initialValue: this.filter.month,
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: false,
            validations: 'required',
            error: 'Favor de seleccionar un mes (0312).',
            class: 'col-md-3',
          },
          {
            name: 'specialistsOrCompanies',
            component: 'CustomDropDown',
            label: 'Proveedor:',
            options: this.specialistsOrCompanies,
            initialValue:
              this.specialistsOrCompanies?.length == 1
                ? this.specialistsOrCompanies[0]
                : null,
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: false,
            class: 'col-md-3',
            validations: 'required',
            error: 'Favor seleccionar la compañía.',
            disabled: this.specialistsOrCompanies?.length == 1,
          },
          {
            name: 'center',
            component: 'CustomDropDown',
            label: 'Centro:',
            options: this.centers,
            initialValue: this.filter.center,
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: false,
            validations: 'required',
            error: 'Favor de seleccionar un Centro (0166).',
            class: 'col-md-3',
          },
          {
            name: 'locationService',
            component: 'CustomDropDown',
            label: 'Lugar de Servicio:',
            options: this.catalog.locationServices,
            initialValue: this.filter.locationService,
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: false,
            validations: 'required',
            error: 'Servicio es requerido (0322).',
            class: 'col-md-3',
            visible: this.hasLevelLocation,
            disabled:
              this.catalog.locationServices &&
              this.catalog.locationServices.length === 1,
          },
          {
            name: 'service',
            component: 'CustomDropDown',
            label: 'Servicio:',
            options: this.services,
            initialValue: this.filter.service,
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: false,
            validations: 'required',
            error: 'Servicio es requerido (0322).',
            class: 'col-md-3',
          },
          {
            name: 'specialist',
            component: 'CustomDropDown',
            label: 'Especialista:',
            options: this.specialists,
            initialValue: this.filter.specialist,
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: false,
            validations: 'required',
            error: 'Especialista es requerido (0245).',
            class: 'col-md-3',
          },
          {
            name: 'status',
            component: 'CustomDropDown',
            label: 'Estatus:',
            options: this.statusList,
            initialValue: this.filter.status,
            placeholder: 'Seleccione',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: false,
            class: 'col-md-3',
          },
        ],
      };
    },
    disabledActionButton() {
      return (status) =>
        [statusTypes.UnSet.id, statusTypes.Pendiente.id].includes(status)
          ? true
          : false;
    },
    getStatusName() {
      return (status) =>
        [statusTypes.UnSet.id, statusTypes.Pendiente.id].includes(status)
          ? statusTypes.Pendiente.name
          : statusTypes.Cargado.name;
    },
    canUploadDocuments() {
      return (
        this.results.length > 0 &&
        this.results[0].billingStatusId == billingStatus.Preliminar
      );
    },
    documentStatusId() {
      return (item) => {
        if (
          item.statusId === statusTypes.Cargado &&
          item.specialistId === this.filterSnapshot.specialist?.id
        ) {
          return statusTypes.Cargado;
        }
        return item.statusId;
      };
    },
    getBorderColor() {
      return (item) => {
        return [statusTypes.UnSet.id, statusTypes.Pendiente.id].includes(
          this.documentStatusId(item)
        )
          ? 'yellow'
          : 'green';
      };
    },
    getDownloadActionColor() {
      return (item) => {
        const status = this.documentStatusId(item);
        if (
          status === statusTypes.Pendiente.id ||
          status == statusTypes.UnSet.id
        ) {
          return 'outline-muted bg-transparent pr-1';
        } else {
          return 'outline-success transparent-color bg-transparent pr-1';
        }
      };
    },
    getEditActionColor() {
      return (item) => {
        const status = this.documentStatusId(item);
        if (
          status === statusTypes.Pendiente.id ||
          status === statusTypes.UnSet.id
        ) {
          return 'outline-muted bg-transparent pr-1';
        }
        if (status === statusTypes.Cargado.id && item.isOpen) {
          return 'outline-orange bg-transparent pr-1';
        }
        if (status === statusTypes.Cargado.id) {
          return 'outline-success transparent-color bg-transparent pr-1';
        }
      };
    },
    hideActionButton() {
      return (status) =>
        [billingStatus.Preliminar].includes(status) ? true : false;
    },
    results: {
      get() {
        return this.resultsModel;
      },
      set(value) {
        this[MUTATION_TYPES.SET_CERTIFICATION_SUMMARY](value);
      },
    },
    hasLevelLocation() {
      return this.userHasPermissions('level.location');
    },
  },
  watch: {
    academicYears() {
      if (this.filter.academicYear == null && this.academicYears.length > 0) {
        let AcademicYearId = this.currentAcademicYear().id;
        this.filter.academicYear = this.academicYears.find(
          (f) => f.id == AcademicYearId
        );
        this[ACTION_TYPES.GET_MONTHS](this.filter.academicYear?.id);
      }
    },
    specialistInfo(value) {
      if (value.errorDocumentCount > 0) {
        this.showError = true;
      }
    },
    specialistsOrCompanies(newValue) {
      if (newValue?.length == 1) {
        this.filter.specialistsOrCompanies = newValue[0];
        if (this.hasLevelLocation) {
          this.getLocations();
        }
      }
    },
  },
  mounted() {
    this[ACTION_TYPES.GET_ACADEMIC_YEARS]();
    this[ACTION_TYPES.GET_DOCUMENT_TYPES]();
    //Get Permission or UserType
    this.validUserType();
  },
  methods: {
    ...mapMutations([
      MUTATION_TYPES.SET_CERTIFICATION_SUMMARY,
      MUTATION_TYPES.SET_CLEAR_CATALOG,
      MUTATION_TYPES.SET_PAGING,
    ]),
    ...mapActions([
      ACTION_TYPES.GET_ACADEMIC_YEARS,
      ACTION_TYPES.GET_MONTHS,
      ACTION_TYPES.GET_CENTERS,
      ACTION_TYPES.GET_SERVICES,
      ACTION_TYPES.GET_SPECIALIST,
      ACTION_TYPES.GET_STUDENT,
      ACTION_TYPES.UPLOAD_FILES,
      ACTION_TYPES.GET_DOCUMENT_TYPES,
      ACTION_TYPES.GET_THERAPY_DOCUMENTS_TYPES,
      ACTION_TYPES.GET_SPECIALIST_OR_COMPANY,
      ACTION_TYPES.GET_SPECIALIST_OR_COMPANY_CENTERS,
    ]),
    onChangeAcademicYear(value) {
      this[ACTION_TYPES.GET_MONTHS](value?.id);
      this.filter.academicYear = value;

      //Clear
      this.filter.month = null;
      this.filter.center = null;
      this.filter.service = null;
      this.filter.specialist = null;
      this.filter.therapyDocumentType = null;
      this.filter.specialistsOrCompanies = null;
      this.filter.status = null;
      this.resetFormValidator();

      this.setClearCatalogInfo({
        months: [],
        centers: [],
        services: [],
        specialists: [],
        therapyDocumentTypes: [],
        specialistsOrCompanies: [],
        statusList: [],
      });
    },
    onChangeMonth(value) {
      this.filter.month = value;
      this.filter.center = null;
      this.filter.service = null;
      this.filter.specialist = null;
      this.filter.therapyDocumentType = null;
      this.filter.specialistsOrCompanies = null;
      this.filter.status = null;
      this.resetFormValidator();
      this.setClearCatalogInfo({
        centers: [],
        services: [],
        specialists: [],
        therapyDocumentTypes: [],
        specialistsOrCompanies: [],
        statusList: [],
      });

      this[ACTION_TYPES.GET_SPECIALIST_OR_COMPANY_CENTERS]({
        academicYearId: this.filter.academicYear?.id,
        month: value,
      });
    },
    onChangeSpecialistsOrCompanies(value) {
      this.filter.specialistsOrCompanies = value;
      this.filter.center = null;
      this.filter.service = null;
      this.filter.specialist = null;
      this.filter.therapyDocumentType = null;
      this.resetFormValidator();

      this.setClearCatalogInfo({
        centers: [],
        services: [],
        specialists: [],
        therapyDocumentTypes: [],
      });
      this[ACTION_TYPES.GET_CENTERS]({
        academicYearId: this.filter.academicYear.id,
        month: this.filter.month.id,
        isAdminOrSpecialist: this.filter.isAdminOrSpecialist,
        specialistId: !this.filter.isAdminOrSpecialist ? value.id : null,
        companyId: this.filter.isAdminOrSpecialist ? value.id : null,
      });
      this.getLocations();
    },
    onChangeCenter(value) {
      this.filter.center = value;
      this.filter.service = null;
      this.filter.specialist = null;
      this.filter.therapyDocumentType = null;
      this.filter.status = null;
      this.resetFormValidator();

      this.setClearCatalogInfo({
        services: [],
        specialists: [],
        therapyDocumentTypes: [],
        statusList: [],
      });

      this[ACTION_TYPES.GET_SERVICES]({
        academicYearId: this.filter.academicYear?.id,
        month: this.filter.month?.id,
        centerId: value?.id,
        companyId: this.filter.specialistsOrCompanies?.id,
      });
    },
    onChangeService(value) {
      this.filter.service = value;
      this.filter.specialist = null;
      this.filter.therapyDocumentType = null;
      this.filter.status = null;
      this.resetFormValidator();

      this.setClearCatalogInfo({
        specialists: [],
        therapyDocumentTypes: [],
        statusList: [],
      });
      this[ACTION_TYPES.GET_SPECIALIST]({
        academicYearId: this.filter.academicYear?.id,
        month: this.filter.month?.id,
        companyId: this.filter.specialistsOrCompanies?.id,
        centerId: this.filter.center?.id,
        serviceId: value?.id,
      });
    },
    onChangeSpecialist(value) {
      this.filter.specialist = value;
      this.filter.therapyDocumentType = null;
      this.filter.status = null;
      this.resetFormValidator();

      this.setClearCatalogInfo({
        therapyDocumentTypes: [],
        statusList: [],
      });

      this[ACTION_TYPES.GET_THERAPY_DOCUMENTS_TYPES]();
    },
    onChangeTherapyDocumentType(value) {
      this.filter.therapyDocumentType = value;
    },
    onChangeStatus(value) {
      this.filter.status = value;
    },
    setClearCatalogInfo(prop) {
      this[MUTATION_TYPES.SET_CLEAR_CATALOG](prop);
    },
    async onSearchHandler() {
      const isValid = await this.$refs.certificationLoadFilterRef.$refs.formValidator.validate();
      if (isValid) {
        this.filterSnapshot = { ...this.filter };
        const payload = {
          academicYearId: this.filter.academicYear?.id,
          companyId: this.filter.specialistsOrCompanies?.id,
          month: this.filter.month?.id,
          centerId: this.filter.center?.id,
          disciplineTypeId: this.filter.service?.id,
          specialistId: this.filter.specialist?.id,
          statusId: this.filter.status
            ? this.filter.status?.id
            : statusTypes.UnSet.id,
          therapyDocumentTypeId: this.filter.therapyDocumentType?.id,
          specialistsOrCompaniesId: this.filter.specialistsOrCompanies?.id,
          locationServiceId: this.filter.locationService?.id,
        };
        this[ACTION_TYPES.GET_STUDENT](payload);
      } else {
        this.ShowToast(
          'Error',
          'Favor de completar la información solicitada para continuar.',
          'error'
        );
      }
    },
    resetAdvancedFilters() {
      this.filter.service = null;
      this.filter.center = null;
      this.filter.specialist = null;
      this.filter.status = null;
      this.filter.therapyDocumentTypes = null;
    },
    goToPage() {},
    toggleError() {
      this.showError = !this.showError;
    },
    getColor(status) {
      switch (status) {
        case statusTypes.Pendiente.id:
        case statusTypes.UnSet.id:
          return Colors.YELLOW;
        case statusTypes.Cargado.id:
          return Colors.GREEN;
        default:
          return '';
      }
    },
    async onDownloadDocument({ documentAttachmentId }) {
      if (documentAttachmentId) {
        await _generalApi.downloadDocumentByAttachmentId(documentAttachmentId);
      }
    },
    onToggleEdit(item) {
      if (this.documentStatusId(item) !== statusTypes.Cargado.id) return;
      this.results = [
        ...this.results.map((s) => {
          if (s.visitCertificationId === item.visitCertificationId) {
            s.isOpen = true;
          } else {
            s.isOpen = false;
          }
          return s;
        }),
      ];
    },
    onCancelEdit(item) {
      this.results = [
        ...this.results.map((s) => {
          if (s.visitCertificationId === item.visitCertificationId) {
            s.isOpen = false;
          }
          return s;
        }),
      ];
    },
    async handleSave(item) {
      const isValid = await this.$refs.editDocumentForm.validate();
      if (!isValid) {
        this.ShowToast('Alerta', 'Información Incompleta', 'error');
      } else {
        var params = {
          academicYearId: this.filterSnapshot.academicYear?.id,
          companyId: this.filterSnapshot.specialistsOrCompanies?.id,
          month: this.filterSnapshot.month?.id,
          centerId: this.filterSnapshot.center?.id,
          disciplineTypeId: this.filterSnapshot.service?.id,
          serviceId: this.filterSnapshot.service?.id,
          specialistId: this.filterSnapshot.specialist?.id,
          statusId: this.filterSnapshot.status
            ? this.filterSnapshot.status?.id
            : statusTypes.UnSet.id,
          studentSieId: item.studentSieId,
          documentTypeId: item.documentType?.id,
        };
        let formData = objectToFormData(
          {
            ...params,
            files: item.selectedDocument,
          },
          {
            indices: true,
            nullsAsUndefineds: true,
          }
        );
        let payload = {
          formData: formData,
          search: params,
        };

        await this[ACTION_TYPES.UPLOAD_FILES](payload);
      }
      // this.$refs.uploadFiles.click();
    },
    async onShowUploadFiles() {
      this.$refs.uploadFiles.click();
    },
    async onUploadFiles(e) {
      if (e.target.files && e.target.files.length > 0) {
        var params = {
          academicYearId: this.filterSnapshot.academicYear?.id,
          companyId: this.filterSnapshot.specialistsOrCompanies?.id,
          month: this.filterSnapshot.month?.id,
          centerId: this.filterSnapshot.center?.id,
          disciplineTypeId: this.filterSnapshot.service?.id,
          serviceId: this.filterSnapshot.service?.id,
          specialistId: this.filterSnapshot.specialist?.id,
          statusId: this.filterSnapshot.status
            ? this.filterSnapshot.status?.id
            : statusTypes.UnSet.id,
        };
        let formData = objectToFormData(
          {
            ...params,
            files: e.target.files,
          },
          {
            indices: true,
            nullsAsUndefineds: true,
          }
        );
        let payload = {
          formData: formData,
          search: params,
        };

        await this[ACTION_TYPES.UPLOAD_FILES](payload);
        this.$refs.uploadFiles.value = null;
      }
    },
    goToPageHandler(page) {
      if (page) {
        this[MUTATION_TYPES.SET_PAGING]({
          ...this.pagination,
          pageIndex: page,
          currentPage: page,
        });
      }
    },
    validUserType() {
      //Valid Permission for specialist, company or companies
      const isAdmin = this.userHasPermissions('visitregistry.management');
      const isSpecialist = this.userRole == roles.Especialista;
      this.filter.isAdminOrSpecialist = isAdmin || isSpecialist;
    },
    resetFormValidator() {
      this.$refs.certificationLoadFilterRef.$refs.formValidator.reset();
    },
    async getLocations() {
      const { data } = await _getServiceLocations(
        this.filter.specialistsOrCompanies.id,
        null,
        null,
        this.filter.academicYear.id
      );
      this.catalog.locationServices = data;

      if (this.catalog.locationServices.length === 1) {
        this.filter.locationService = this.catalog.locationServices[0];
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/shared/scss/config/_colors.scss';
.info-container {
  display: flex;
  flex-wrap: wrap;
}
.info-panel {
  padding: 15px 10px 1px 20px;
  .info-panel-main-title {
    em {
      color: map-get($colors, 'orange');
    }
    span {
      text-align: left;
      font: Bold 16px/19px Lato;
      letter-spacing: 0px;
      color: #3f5b60;
      opacity: 1;
      margin-left: 7px;
    }
  }
  .info-panel-container {
    padding: 15px 10px 1px 23px;
  }
}

.dot {
  box-shadow: 0px 3px 6px #00000029;
  margin: 2px;
  width: 27px;
  height: 27px;
  border-radius: 50%;
  border-style: solid;
  border-width: 3px;
  border-color: map-get($colors, 'white');
  display: inline-block;
  margin-right: 15px;
  .title-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 1px;
    font-size: 20px;
  }
}

.transparent-color:hover {
  color: map-get($colors, 'green') !important;
}

.no-info {
  background: #f7f7f7 0% 0% no-repeat padding-box;
  border-radius: 5px;
  opacity: 1;
  border-left-style: solid;
  border-color: #e8e8e8;
  border-width: 0;
  .no-info-text {
    text-align: left;
    font: Bold 16px/22px Lato;
    letter-spacing: 0;
    color: #727272;
    opacity: 1;
  }
  .no-info-icon {
    color: #727272;
    font-size: 32px;
  }
}

.main-box {
  display: flex;
  background: #fff5e8 0% 0% no-repeat padding-box;
  border: 1px solid #fdac41;
  border-radius: 4px;
  opacity: 1;
  padding: 10px;
  margin-top: 10px;
  margin-left: 14px;
}

.main-box i {
  color: #af6708;
  opacity: 1;
  margin-right: 10px;
  margin-top: 5px;
}

.main-box span {
  text-align: left;
  font: Regular 14px/24px Lato;
  letter-spacing: 0;
  color: #af6708;
  opacity: 1;
}
</style>
